/**
 * Notification Service
 * Handles API calls for notifications
 */
import api from './api';

const NOTIFICATIONS_BASE = '/api/user/notifications';

/**
 * Get all notifications with pagination
 * @param {number} page - Page number (0-indexed)
 * @param {number} size - Number of items per page
 * @param {boolean} unreadOnly - Filter to unread only
 * @returns {Promise<Object>} Paginated notifications
 */
export const getNotifications = async (
  page = 0,
  size = 20,
  unreadOnly = false
) => {
  const response = await api.get(NOTIFICATIONS_BASE, {
    params: { page, size, unreadOnly },
  });
  return response.data;
};

/**
 * Get unread notification count
 * @returns {Promise<number>} Number of unread notifications
 */
export const getUnreadCount = async () => {
  const response = await api.get(`${NOTIFICATIONS_BASE}/unread/count`);
  return response.data.count;
};

/**
 * Get a specific notification
 * @param {string} id - Notification ID
 * @returns {Promise<Object>} Notification object
 */
export const getNotification = async (id) => {
  const response = await api.get(`${NOTIFICATIONS_BASE}/${id}`);
  return response.data;
};

/**
 * Mark a notification as read
 * @param {string} id - Notification ID
 * @returns {Promise<void>}
 */
export const markAsRead = async (id) => {
  await api.patch(`${NOTIFICATIONS_BASE}/${id}/read`);
};

/**
 * Mark all notifications as read
 * @returns {Promise<Object>} Object with markedCount
 */
export const markAllAsRead = async () => {
  const response = await api.patch(`${NOTIFICATIONS_BASE}/read-all`);
  return response.data;
};

/**
 * Delete a notification
 * @param {string} id - Notification ID
 * @returns {Promise<void>}
 */
export const deleteNotification = async (id) => {
  await api.delete(`${NOTIFICATIONS_BASE}/${id}`);
};

/**
 * Notification type icons and colors mapping
 */
export const NOTIFICATION_TYPES = {
  payment_confirmed: {
    icon: 'Receipt',
    color: 'text-[var(--success)]',
    bgColor: 'bg-[var(--success)]/10',
  },
  payment_failed: {
    icon: 'XCircle',
    color: 'text-[var(--error)]',
    bgColor: 'bg-[var(--error)]/10',
  },
  payment_disputed: {
    icon: 'AlertTriangle',
    color: 'text-[var(--warning)]',
    bgColor: 'bg-[var(--warning)]/10',
  },
  document_uploaded: {
    icon: 'FileText',
    color: 'text-[var(--info)]',
    bgColor: 'bg-[var(--info)]/10',
  },
  documents_ready: {
    icon: 'FileCheck',
    color: 'text-[var(--success)]',
    bgColor: 'bg-[var(--success)]/10',
  },
  filing_submitted: {
    icon: 'Clock',
    color: 'text-[var(--info)]',
    bgColor: 'bg-[var(--info)]/10',
  },
  filing_completed: {
    icon: 'CheckCircle',
    color: 'text-[var(--success)]',
    bgColor: 'bg-[var(--success)]/10',
  },
  filing_rejected: {
    icon: 'XCircle',
    color: 'text-[var(--error)]',
    bgColor: 'bg-[var(--error)]/10',
  },
  compliance_reminder: {
    icon: 'AlertCircle',
    color: 'text-[var(--warning)]',
    bgColor: 'bg-[var(--warning)]/10',
  },
  security_alert: {
    icon: 'Shield',
    color: 'text-[var(--error)]',
    bgColor: 'bg-[var(--error)]/10',
  },
  system_alert: {
    icon: 'Info',
    color: 'text-[var(--info)]',
    bgColor: 'bg-[var(--info)]/10',
  },
  ticket_created: {
    icon: 'MessageSquare',
    color: 'text-[var(--info)]',
    bgColor: 'bg-[var(--info)]/10',
  },
  ticket_updated: {
    icon: 'MessageCircle',
    color: 'text-[var(--info)]',
    bgColor: 'bg-[var(--info)]/10',
  },
  default: {
    icon: 'Bell',
    color: 'text-[var(--rely-text-muted)]',
    bgColor: 'bg-[var(--rely-surface-elevated)]',
  },
};

/**
 * Get notification type configuration
 * @param {string} type - Notification type
 * @returns {Object} Icon and color configuration
 */
export const getNotificationTypeConfig = (type) => {
  return NOTIFICATION_TYPES[type] || NOTIFICATION_TYPES.default;
};

const notificationService = {
  getNotifications,
  getUnreadCount,
  getNotification,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  getNotificationTypeConfig,
  NOTIFICATION_TYPES,
};

export default notificationService;
